import React from "react";
import { useMutation } from "@tanstack/react-query";
import { Form, InputNumber, Typography, Row, Col, Button, Alert } from "antd";
import { useForm } from "antd/es/form/Form";
import {
  RevenueConfiguration,
  SiteReadable as Site,
} from "../../services/client/types.gen";
import { updateRevenueConfigurationMutation } from "../../services/client/@tanstack/react-query.gen";
import { useNotify } from "../util-components/Notifications/notifications";
import { useIsViewer } from "../../hooks/useIsViewer";
import { DisableForViewer } from "../common/DisableForViewer";

const { Title } = Typography;

type ChargingRevenueFormProps = {
  site: Site;
  refetchSite: () => void;
};

export const ChargingRevenueForm: React.FC<ChargingRevenueFormProps> = ({
  site,
  refetchSite,
}) => {
  const [form] = useForm<RevenueConfiguration>();
  const notify = useNotify();
  const isViewer = useIsViewer();

  const updateRevenue = useMutation({
    ...updateRevenueConfigurationMutation(),
  });

  const onFinish = (values: RevenueConfiguration) => {
    if (!site.site_id) {
      return;
    }

    updateRevenue.mutate(
      {
        path: { site_id: site.site_id },
        body: {
          revenue_per_kwh_ac_charged: values.revenue_per_kwh_ac_charged ?? null,
          revenue_per_kwh_dc_charged: values.revenue_per_kwh_dc_charged ?? null,
        },
      },
      {
        onError: () => {
          notify({
            type: "error",
            message: "Something went wrong while updating the revenue.",
          });
        },
        onSuccess: () => {
          refetchSite();
          notify({
            type: "success",
            message: "Charging revenue updated successfully!",
          });
        },
      },
    );
  };

  return (
    <div className="mt-8">
      <Title level={3}>Charging Revenue</Title>
      <Typography.Paragraph type="secondary" className="mb-4">
        What you earn per kWh sold to drivers. We use it to report how much
        extra revenue the energy delivered by Pleevi generates compared to plain
        load-balancing.
      </Typography.Paragraph>
      <Form
        form={form}
        initialValues={site.configurations?.revenue ?? undefined}
        onFinish={onFinish}
        layout="vertical"
        className="bg-gray-50 p-4 rounded-md border-gray-100 border border-solid"
        disabled={isViewer}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="revenue_per_kwh_ac_charged"
              label="AC Revenue per kWh charged"
            >
              <InputNumber
                placeholder="AC Revenue"
                addonAfter="€/kWh"
                style={{ width: "100%" }}
                step={0.01}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="revenue_per_kwh_dc_charged"
              label="DC Revenue per kWh charged"
            >
              <InputNumber
                placeholder="DC Revenue"
                addonAfter="€/kWh"
                style={{ width: "100%" }}
                step={0.01}
              />
            </Form.Item>
          </Col>
        </Row>

        <DisableForViewer>
          <Button
            type="primary"
            htmlType="submit"
            loading={updateRevenue.isPending}
          >
            Update Revenue
          </Button>
        </DisableForViewer>

        {updateRevenue.isError && (
          <Alert
            message="Error"
            description="Something went wrong while updating the charging revenue."
            type="error"
            showIcon
            className="mt-4"
          />
        )}
      </Form>
    </div>
  );
};

export default ChargingRevenueForm;
